import useAuth from '@app/auth/use-auth';
import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { getReportsPathMap } from '@app/components/reports/utils';
import IngredientEmissionsInsight from '@app/pcf/ingredients/components/ingredient-emissions-insights';
import { IngredientList, IngredientModelledAsEnum } from '@shared/api/types';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import Unit from '@shared/components/content/unit';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { ColumnDef } from '@tanstack/react-table';
import { IngredientFilterColumn, IngredientFilterOption } from './filters';

import { Icons } from '@shared/components/content/icons';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import RatingCloud, {
  RatingCloudProps,
} from '@shared/components/ratings/rating-cloud';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { formatSmallNumber } from '@shared/lib/utils';
import { generatePath } from 'react-router-dom';

export function useIngredientsTableColumns({
  filters,
}: {
  filters: FilterOptions<IngredientFilterOption[]>;
}): ColumnDef<IngredientList>[] {
  const { user } = useAuth();
  const hasPurchasedGoods = user?.features.scope3;

  return [
    {
      id: 'baseFoodUuid',
      accessorKey: 'baseFoodUuid',
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => (
        <div data-testid="ingredient-column-header">
          <HeaderCell column={column} name="Ingredient" />
        </div>
      ),
      cell: ({ getValue, row }) => {
        const name = getValue<string>();

        return (
          <Text className="max-w-3xl truncate" includeHover>
            {name}
          </Text>
        );
      },
      enableHiding: false,
      enableColumnFilter: true,
      meta: {
        label: 'Search',
        variant: 'text',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'insights',
      accessorKey: 'infoMarkdown',
      header: () => {},
      cell: ({ row }) => {
        const { infoMarkdown, name, emissionsCategory } = row.original;
        return (
          infoMarkdown && (
            <IngredientEmissionsInsight
              emissionsCategory={emissionsCategory}
              markdown={infoMarkdown}
              longDesc={name}
              className="z-50 w-20"
            />
          )
        );
      },
      enableSorting: false,
    },
    {
      id: 'matchedFood',
      accessorKey: 'matchedFood',
      accessorFn: (row) => row,
      header: ({ column }) => (
        <HeaderCell column={column} name="Matched food" />
      ),
      cell: ({ getValue, row }) => {
        const modelledBy = row.original.modelledAs;
        const ingredient = getValue<IngredientList>();

        switch (modelledBy) {
          case IngredientModelledAsEnum.purchased_good:
            if (!hasPurchasedGoods) {
              return (
                <span className="flex max-w-80 items-center gap-1">
                  <Icons.shoppingCart className="size-4 shrink-0 text-gray-500" />
                  <Text className="truncate">
                    {ingredient.purchasedGoodName}
                  </Text>
                </span>
              );
            }
            return (
              <span className="flex max-w-80 items-center gap-1">
                <Icons.shoppingCart className="size-4 shrink-0 text-gray-500" />
                <TextLink
                  className="truncate"
                  to={generatePath(getReportsPathMap().purchase.foodItem!, {
                    item_uuid: ingredient.purchasedGoodActivityUuid,
                  })}
                >
                  {ingredient.purchasedGoodName}
                </TextLink>
              </span>
            );

          case IngredientModelledAsEnum.external_emission_factor:
            return (
              <span className="flex max-w-80 items-center gap-1 truncate text-nowrap">
                <Icons.arrowRightCircle className="size-4 shrink-0 text-gray-500" />
                <Text className="text-gray-600" includeHover>
                  External product assessment
                </Text>
              </span>
            );

          case IngredientModelledAsEnum.modelled_food:
            return (
              <span className="flex max-w-80 items-center gap-1 truncate text-nowrap">
                <SimpleTooltip
                  trigger={
                    <Icons.chefHat className="size-4 shrink-0 text-gray-500" />
                  }
                  content="This food item has been modelled using a custom recipe"
                />
                <Text includeHover>{ingredient.name}</Text>
              </span>
            );

          default:
            return <p>{ingredient.baseFoodName}</p>;
        }
      },
      enableSorting: false,
    },
    {
      id: 'emissionsFactor',
      accessorKey: 'emissionsFactor',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emission factor"
          numberColumn
          unit={<Unit variant="kgCO2e/kgfood" />}
        />
      ),
      cell: ({ getValue, row }) => {
        const carbonIntensity: number = getValue<number>();
        const emissionsCategory = row.original.emissionsCategory;
        return (
          (carbonIntensity != null && (
            <span className="flex items-center justify-end gap-2">
              <TextCell
                text={formatSmallNumber(carbonIntensity)}
                type="number"
              />
              <RatingCloud
                className="w-10"
                rating={emissionsCategory as RatingCloudProps['rating']}
              />
            </span>
          )) ??
          null
        );
      },
    },
    {
      id: 'numOfProducts',
      accessorKey: 'numOfProducts',
      header: ({ column }) => (
        <HeaderCell column={column} name="Products using" numberColumn />
      ),
      cell: ({ getValue }) => {
        const numOfProducts: number = getValue<number>();
        return <NumberCell number={numOfProducts} rounded />;
      },
    },
    {
      id: IngredientFilterColumn.EmissionsCategory,
      accessorKey: IngredientFilterColumn.EmissionsCategory,
      enableColumnFilter: true,
      meta: {
        label: 'Rating',
        variant: 'multiSelect',
        options: filters.emissionsCategory,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
  ];
}
